import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { can, useOrg } from "../org";
import { CARE_STATE, Field, REL, Select, fromPco } from "./bits";

const BASIC = ["first_name", "last_name", "preferred_name", "birth_date", "email", "phone", "household_id", "household_relationship"];
const CARE = ["care_state", "caregiver_id", "active_rhythm_days", "follow_up_on"];

const Text = ({ f, k, set, type = "text", testid, locked }) => (
  <input type={type} value={f[k] ?? ""} disabled={locked} onChange={(e) => set(k, e.target.value)} className="field h-12 px-3 disabled:opacity-60" data-testid={testid} />
);

const CareFields = ({ f, set, caregivers }) => (
  <div className="border-t border-line pt-5 mt-2 space-y-4" data-testid="person-form-care">
    <Field label="Care state">
      <div className="flex flex-wrap gap-2">
        {Object.entries(CARE_STATE).map(([k, s]) => (
          <button type="button" key={k} aria-pressed={f.care_state === k} onClick={() => set("care_state", k)} className="choice h-11 px-4" data-testid={`person-form-state-${k}`}>{s.label}</button>
        ))}
      </div>
    </Field>
    <Field label="Assigned Care Leader">
      <Select value={f.caregiver_id} onChange={(v) => set("caregiver_id", v)} testid="person-form-caregiver">
        <option value="">No one yet</option>
        {!caregivers.length && f.caregiver_id && <option value={f.caregiver_id}>Loading…</option>}
        {caregivers.map((c) => <option key={c.id} value={c.id}>{c.name} · caring for {c.caring_for}</option>)}
      </Select>
    </Field>
    {f.care_state === "active" && (
      <Field label="Active care rhythm (days, optional)"><Text f={f} k="active_rhythm_days" set={set} type="number" testid="person-form-active-rhythm" /></Field>
    )}
    <Field label="Scheduled check-in (optional)"><Text f={f} k="follow_up_on" set={set} type="date" testid="person-form-follow-up" /></Field>
  </div>
);

export const PersonForm = ({ person, onClose, onSaved }) => {
  const { api, membership } = useOrg();
  const c = can(membership);
  const [f, setF] = useState(() => ({ care_state: "regular", ...(person || {}), caregiver_id: person?.caregiver?.id ?? null }));
  const [homes, setHomes] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [newHome, setNewHome] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const locked = fromPco(person);
  useEffect(() => {
    api("/households").then(setHomes).catch(() => {});
    if (c.care) api("/caregivers").then(setCaregivers).catch(() => {});
  }, [api, c.care]);
  const save = async () => {
    const keys = c.care ? [...BASIC, ...CARE] : BASIC;
    const orig = person ? { ...person, caregiver_id: person.caregiver?.id ?? null } : {};
    const norm = (v) => (v === "" || v === undefined ? null : v);
    const body = Object.fromEntries(keys.filter((k) => !person || norm(f[k]) !== norm(orig[k])).map((k) => [k, norm(f[k])]));
    if (body.active_rhythm_days) body.active_rhythm_days = Number(body.active_rhythm_days);
    if (body.household_id === "__new") { body.household_id = null; body.new_household_name = newHome; }
    setBusy(true);
    try {
      const p = await api(person ? `/people/${person.id}` : "/people", { method: person ? "PATCH" : "POST", body });
      toast.success(person ? "Saved" : `${p.first_name} ${p.last_name} was added`);
      onSaved(p);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-paper max-w-lg max-h-[92dvh] overflow-y-auto" data-testid="person-form">
        <DialogTitle className="t-name-lg">{person ? "Edit person" : "Add a person"}</DialogTitle>
        <DialogDescription className="t-meta" data-testid="person-form-note">{locked ? "Name, contact details and household come from Planning Center. Update them there." : "Fictional or manual records only for now."}</DialogDescription>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="First name"><Text f={f} k="first_name" set={set} locked={locked} testid="person-form-first" /></Field>
          <Field label="Last name"><Text f={f} k="last_name" set={set} locked={locked} testid="person-form-last" /></Field>
          <Field label="Goes by"><Text f={f} k="preferred_name" set={set} testid="person-form-preferred" /></Field>
          <Field label="Birth date"><Text f={f} k="birth_date" set={set} type="date" testid="person-form-birth" /></Field>
          <Field label="Email"><Text f={f} k="email" set={set} type="email" locked={locked} testid="person-form-email" /></Field>
          <Field label="Phone"><Text f={f} k="phone" set={set} type="tel" locked={locked} testid="person-form-phone" /></Field>
        </div>
        {!locked && <div className="grid sm:grid-cols-2 gap-3 mt-1">
          <Field label="Household">
            <Select value={f.household_id} onChange={(v) => set("household_id", v)} testid="person-form-household">
              <option value="">No household</option>
              <option value="__new">+ New household</option>
              {homes.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </Select>
          </Field>
          {f.household_id && (
            <Field label="Relationship">
              <Select value={f.household_relationship} onChange={(v) => set("household_relationship", v)} testid="person-form-relationship">
                <option value="">Choose…</option>
                {Object.entries(REL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Select>
            </Field>
          )}
        </div>}
        {locked && f.household_id && (
          <Field label="Relationship in household">
            <Select value={f.household_relationship} onChange={(v) => set("household_relationship", v)} testid="person-form-relationship">
              <option value="">Choose…</option>
              {Object.entries(REL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </Select>
          </Field>
        )}
        {f.household_id === "__new" && <input value={newHome} onChange={(e) => setNewHome(e.target.value)} placeholder="e.g. Pettersen household" className="field h-12 px-3" data-testid="person-form-new-household" />}
        {c.care && <CareFields f={f} set={set} caregivers={caregivers} />}
        <button disabled={busy || !f.first_name || !f.last_name} onClick={save} className="btn btn-primary w-full mt-4" data-testid="person-form-save">{person ? "Save changes" : "Add person"}</button>
      </DialogContent>
    </Dialog>
  );
};
